import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaShieldAlt, FaCog } from 'react-icons/fa';
import { Container, Section, Button, theme } from '../../styles/GlobalStyles';

const ContentContainer = styled.div`
  display: grid;
  gap: 100px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 60px;
  }
`;

const ContentSection = styled.div<{ reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  ${({ reverse }) => reverse && `
    direction: rtl;
    & > * {
      direction: ltr;
    }
  `}

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
    direction: ltr;
    
    & > * {
      direction: ltr;
    }
  }
`;

const ContentText = styled(motion.div)`
  padding: 20px 0;
`;

const ContentTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const ContentDescription = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 30px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
  font-size: 1rem;
  color: ${theme.colors.text};

  svg {
    color: ${theme.colors.accent};
    font-size: 1.2rem;
  }
`;

const ImagePlaceholder = styled(motion.div)<{ bgColor?: string }>`
  height: 400px;
  background: ${({ bgColor }) => bgColor || theme.gradients.card};
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: 1px solid ${theme.colors.border};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%2306b6d4" fill-opacity="0.1"><circle cx="20" cy="20" r="2"/></g></g></svg>');
  }

  svg {
    font-size: 4rem;
    color: ${theme.colors.accent};
    opacity: 0.3;
    z-index: 1;
    position: relative;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const ContentSections: React.FC = () => {
  const sections = [
    {
      title: 'Combine all your cybersecurity standards and compliance mandates in one effort',
      description: 'Streamline your compliance journey with our integrated approach to cybersecurity standards. We help organizations achieve multiple certifications including ISO 27001, PCI DSS, and regulatory compliance requirements through unified processes and documentation.',
      features: [
        'Unified compliance framework implementation',
        'Gap analysis and remediation planning',
        'Continuous monitoring and assessment',
        'Expert guidance from certified professionals',
        'Cost-effective multi-standard approach'
      ],
      icon: <FaShieldAlt />,
      bgColor: theme.gradients.accent,
      reverse: false
    },
    {
      title: 'State-of-the-art managed security services',
      description: 'Our comprehensive managed security services provide round-the-clock protection with advanced threat detection, incident response, and proactive security monitoring. Leverage our expert team and cutting-edge technologies to secure your digital assets.',
      features: [
        '24/7 Security Operations Center (SOC)',
        'Advanced threat detection and response',
        'Incident management and forensics',
        'Compliance monitoring and reporting',
        'Proactive threat hunting services'
      ],
      icon: <FaCog />,
      bgColor: theme.gradients.card,
      reverse: true
    }
  ];

  return (
    <Section>
      <Container>
        <ContentContainer>
          {sections.map((section, index) => (
            <ContentSection key={index} reverse={section.reverse}>
              <ContentText
                initial={{ opacity: 0, x: section.reverse ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <ContentTitle>{section.title}</ContentTitle>
                <ContentDescription>{section.description}</ContentDescription>
                
                <FeatureList>
                  {section.features.map((feature, featureIndex) => (
                    <FeatureItem key={featureIndex}>
                      <FaCheckCircle />
                      {feature}
                    </FeatureItem>
                  ))}
                </FeatureList>

                <Button variant="primary">
                  Learn More
                </Button>
              </ContentText>

              <ImagePlaceholder
                bgColor={section.bgColor}
                initial={{ opacity: 0, x: section.reverse ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ scale: 1.02 }}
              >
                {section.icon}
              </ImagePlaceholder>
            </ContentSection>
          ))}
        </ContentContainer>
      </Container>
    </Section>
  );
};

export default ContentSections;

